import React, { useState, useEffect } from 'react';
import NavigationBar from '../NavigationBar/NavigationBar';
import { useLocation, useNavigate } from 'react-router-dom';

const SavedMaps = () => {
    const [savedMaps, setSavedMaps] = useState([]);
    const [userProfile, setUserProfile] = useState(null);
    const [anonymousUserId, setAnonymousUserId] = useState(null);
    const [researchId, setResearchId] = useState(null);
    const [previewMap, setPreviewMap] = useState(null);
    const navigate = useNavigate();

    useEffect(() => {
        // Check if user is logged in by looking for data in localStorage or session
        const checkAuth = () => {
            const storedProfile = localStorage.getItem('userProfile');
            const storedUserId = localStorage.getItem('anonymousUserId');
            const storedResearchId = localStorage.getItem('researchId');
            
            if (!storedProfile || !storedUserId) {
                navigate('/'); // Redirect to login if not authenticated
                return;
            }

            setUserProfile(JSON.parse(storedProfile));
            setAnonymousUserId(storedUserId);
            setResearchId(storedResearchId);

            // Load saved maps
            const maps = JSON.parse(localStorage.getItem(`savedMaps_${storedUserId}`)) || [];
            setSavedMaps(maps);
        };

        checkAuth();
    }, [navigate]);

    const handleLogout = async () => {
        localStorage.removeItem('userProfile');
        localStorage.removeItem('anonymousUserId');
        localStorage.removeItem('researchId');
        navigate('/');
    };

    const handleDelete = (indexToDelete) => {
        const updatedMaps = savedMaps.filter((_, index) => index !== indexToDelete);
        localStorage.setItem(`savedMaps_${anonymousUserId}`, JSON.stringify(updatedMaps));
        setSavedMaps(updatedMaps);
    };

    const handleDownload = (imageData) => {
        // Create a temporary link element
        const link = document.createElement('a');
        link.href = imageData;
        link.download = `map-export-${new Date().toISOString()}.png`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    return (
        <div style={{ 
            minHeight: '100vh', 
            display: 'flex', 
            flexDirection: 'column',
            background: '#f8f9fa'  // Light gray background
        }}>
            <NavigationBar
                userProfile={userProfile}
                anonymousUserId={anonymousUserId}
                researchId={researchId}
                logoutFunction={handleLogout}
            />
            
            <div style={{
                padding: '32px',
                paddingTop: '96px',
                maxWidth: '1400px',  // Increased max-width
                margin: '0 auto',
                flex: 1,
                width: '100%'
            }}>
                <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '32px'
                }}>
                    <h1 style={{
                        fontSize: '28px',
                        color: '#1a1a1a',
                        fontWeight: '600',
                        letterSpacing: '-0.5px'
                    }}>
                        Library
                    </h1>
                    <span style={{
                        color: '#666',
                        fontSize: '15px'
                    }}>
                        {savedMaps.length} saved map{savedMaps.length !== 1 ? 's' : ''}
                    </span>
                </div>

                <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
                    gap: '20px',
                    padding: '4px'
                }}>
                    {savedMaps.map((map, index) => (
                        <div key={index} style={{
                            background: 'white',
                            borderRadius: '12px',
                            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
                            overflow: 'hidden',
                            cursor: 'pointer',
                            transition: 'all 0.3s ease',
                            ':hover': {
                                transform: 'translateY(-4px)',
                                boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12)'
                            }
                        }}>
                            <div style={{
                                padding: '16px',
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '12px'
                            }}>
                                <div style={{
                                    position: 'relative',
                                    borderRadius: '12px',
                                    overflow: 'hidden',
                                    boxShadow: '0 2px 12px rgba(0, 0, 0, 0.1)'
                                }}>
                                    <img 
                                        src={map.imageData} 
                                        alt={`Saved map ${index + 1}`}
                                        onClick={() => setPreviewMap(map)}
                                        style={{
                                            width: '100%',
                                            display: 'block',
                                            cursor: 'zoom-in'
                                        }}
                                    />
                                </div>

                                <div style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: '12px'
                                }}>
                                    <div style={{
                                        fontSize: '13px',
                                        color: '#666',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '6px'
                                    }}>
                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <circle cx="12" cy="12" r="10"/>
                                            <path d="M12 6v6l4 2"/>
                                        </svg>
                                        {new Date(map.savedAt).toLocaleString()}
                                    </div>

                                    <div style={{
                                        display: 'flex',
                                        gap: '8px'
                                    }}>
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleDownload(map.imageData);
                                            }}
                                            style={{
                                                padding: '10px',
                                                background: '#4a86e8',
                                                color: 'white',
                                                border: 'none',
                                                borderRadius: '8px',
                                                cursor: 'pointer',
                                                flex: '1',
                                                transition: 'all 0.2s ease',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                gap: '6px',
                                                fontSize: '14px',
                                                fontWeight: '500'
                                            }}
                                        >
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                                                <polyline points="7 10 12 15 17 10"/>
                                                <line x1="12" y1="15" x2="12" y2="3"/>
                                            </svg>
                                            Download
                                        </button>
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleDelete(index);
                                            }}
                                            style={{
                                                padding: '10px',
                                                background: '#ff4444',
                                                color: 'white',
                                                border: 'none',
                                                borderRadius: '8px',
                                                cursor: 'pointer',
                                                transition: 'all 0.2s ease',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                width: '42px'
                                            }}
                                        >
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                <path d="M3 6h18"/>
                                                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6"/>
                                                <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                                            </svg>
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Preview Modal */}
            {previewMap && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0,0,0,0.8)',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    zIndex: 1200,
                    cursor: 'pointer'
                }} onClick={() => setPreviewMap(null)}>
                    <div style={{
                        position: 'relative',
                        maxWidth: '90%',
                        maxHeight: '90%',
                        overflow: 'auto',
                        backgroundColor: 'white',
                        padding: '20px',
                        borderRadius: '12px',
                        cursor: 'default'
                    }} onClick={e => e.stopPropagation()}>
                        <button
                            onClick={() => setPreviewMap(null)}
                            style={{
                                position: 'absolute',
                                top: '10px',
                                right: '10px',
                                background: 'white',
                                border: 'none',
                                borderRadius: '50%',
                                width: '30px',
                                height: '30px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                cursor: 'pointer',
                                boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                            }}
                        >
                            ✕
                        </button>
                        <img 
                            src={previewMap.imageData} 
                            alt="Preview"
                            style={{
                                maxWidth: '100%',
                                maxHeight: 'calc(90vh - 40px)',
                                objectFit: 'contain'
                            }}
                        />
                        <div style={{
                            marginTop: '16px',
                            textAlign: 'right',
                            color: '#666',
                            fontSize: '14px'
                        }}>
                            Saved on {new Date(previewMap.savedAt).toLocaleString()}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default SavedMaps; 